import { IAgoraRTCClient, UID, useClientEvent } from 'agora-rtc-react'
import { AgoraLogContext, logAgoraEvent } from 'src/lib/datadog/agoraLogger'

// original definition of network quality levels from Agora docs:
//   0: The quality is unknown.
//   1: The quality is excellent.
//   2: The quality is good, but the bitrate is less than optimal.
//   3: Users experience slightly impaired communication.
//   4: Users can communicate with each other, but not very smoothly.
//   5: The quality is so poor that users can barely communicate.
//   6: The network is disconnected and users cannot communicate.
const networkQualityLabels: Record<0 | 1 | 2 | 3 | 4 | 5 | 6, string> = {
  0: 'unknown',
  1: 'excellent',
  2: 'good',
  3: 'slightly impaired',
  4: 'not very smooth',
  5: 'very poor',
  6: 'disconnected',
}

type Props = {
  client: IAgoraRTCClient
  logContext: AgoraLogContext
}

// Agoraで発生したイベントを検知し、Datadogに送信する
export const useAgoraLogger = ({
  client,
  logContext: baseLogContext,
}: Props): void => {
  // 接続状態の遷移を記録する。切断・再接続の原因を把握するために常に送信する
  useClientEvent(
    client,
    'connection-state-change',
    (curState, prevState, reason) => {
      logAgoraEvent('info', `connection-state-change: ${curState}`, {
        ...baseLogContext,
        type: 'connection-state-change',
        curState,
        prevState,
        reason,
      })
    },
  )

  // SDK が検出した内部例外・回復イベントを記録する。
  // code 1000-2999 は例外発生（warn）、code 3000-4999 は例外からの回復（info）
  useClientEvent(client, 'exception', event => {
    const level = event.code < 3000 ? 'warn' : 'info'
    logAgoraEvent(level, 'Agora exception', {
      ...baseLogContext,
      type: 'exception',
      code: event.code,
      msg: event.msg,
      uid: event.uid,
    })
  })

  useClientEvent(client, 'network-quality', stats => {
    const worst = Math.max(
      stats.uplinkNetworkQuality,
      stats.downlinkNetworkQuality,
    )
    // 3-5: 通話に支障あり → warn
    // 6: 切断 → error
    const level = worst >= 6 ? 'error' : worst >= 3 ? 'warn' : 'info'
    const comment = `network quality: uplink ${stats.uplinkNetworkQuality}(${networkQualityLabels[stats.uplinkNetworkQuality]}), downlink ${stats.downlinkNetworkQuality} (${networkQualityLabels[stats.downlinkNetworkQuality]})`
    logAgoraEvent(level, comment, {
      ...baseLogContext,
      type: 'network-quality',
      uplinkNetworkQuality: stats.uplinkNetworkQuality,
      downlinkNetworkQuality: stats.downlinkNetworkQuality,
    })
  })

  // ネットワーク悪化により映像がオーディオのみに劣化、または回復したことを記録する
  useClientEvent(client, 'stream-fallback', (uid: UID, isFallbackOrRecover) => {
    const level = isFallbackOrRecover === 'fallback' ? 'warn' : 'info'
    logAgoraEvent(level, 'Agora stream fallback', {
      ...baseLogContext,
      type: 'stream-fallback',
      uid,
      isFallbackOrRecover,
    })
  })

  // メディアの再接続開始・完了を記録する
  useClientEvent(client, 'media-reconnect-start', (uid: UID) => {
    logAgoraEvent('warn', 'Agora media reconnect start', {
      ...baseLogContext,
      type: 'media-reconnect-start',
      uid,
    })
  })
  useClientEvent(client, 'media-reconnect-end', (uid: UID) => {
    logAgoraEvent('info', 'Agora media reconnect end', {
      ...baseLogContext,
      type: 'media-reconnect-end',
      uid,
    })
  })

  // 通常の UDP 経路に接続できず TCP/TLS 443 のプロキシ経由でチャンネルに参加したことを記録する
  useClientEvent(client, 'join-fallback-to-proxy', (proxyServer: string) => {
    logAgoraEvent('info', 'Agora join fallback to proxy', {
      ...baseLogContext,
      type: 'join-fallback-to-proxy',
      proxyServer,
    })
  })
}
